import HeadData from 'components/Common/HeadData'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { isBedbankOffer, isHotel } from 'lib/offer/offerTypes'
import config from 'constants/config'
import { resolveLocalizedConfigValue } from 'constants/config/resolveLocalizedConfigValue'
import {
  getCheckInDateFromURLSearchParams,
  getCheckOutDateFromURLSearchParams,
  getFlexibleMonthsFromURLSearchParams,
  getFlexibleNightsFromURLSearchParams,
} from 'lib/url/searchUrlUtils'
import useQueryParams from 'hooks/useQueryParams'
import { getPageTitle } from 'components/Search/utils'
import type { FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { getHeadDescription } from 'components/OfferPage/utils'
import type { SearchFilterParams } from 'lib/search/searchUtils'
import MetaSearchStructuredData from 'components/Pages/HotelSearchPage/MetaSearchStructuredData'

interface Props {
  offer: App.BedbankOffer
  searchParams: SearchFilterParams
  filters: App.OfferListFilters
}

function MetaSearchPageHeadData(props: Props) {
  const { offer, searchParams, filters } = props
  const intl = useIntl()
  const queryParams = useQueryParams()
  const luxuryHotelFallbackName = intl.formatMessage({
    id: 'HKJViPBpFCTQAQ6DQ151',
    defaultMessage: 'Luxury Hotel',
    description: 'Fallback property name used on the meta search page when a hotel offer name is unavailable or not a hotel',
  })

  const pageTitle = useMemo(() => {
    const { searchItem } = searchParams
    const checkInDate = getCheckInDateFromURLSearchParams(queryParams)
    const checkOutDate = getCheckOutDateFromURLSearchParams(queryParams)
    const flexibleMonths = getFlexibleMonthsFromURLSearchParams(queryParams)
    const flexibleNights = getFlexibleNightsFromURLSearchParams(queryParams)
    const propertyName = isHotel(offer) ? offer.property?.name : luxuryHotelFallbackName

    return getPageTitle(intl, {
      ...searchItem,
      format: { mainText: propertyName },
      searchType: 'property',
      value: '',
    }, { checkIn: checkInDate, checkOut: checkOutDate, flexibleMonths, flexibleNights: flexibleNights as FLEXIBLE_DURATION_RANGE })
  }, [intl, queryParams, searchParams, offer, luxuryHotelFallbackName])

  const pageDescription = useMemo(() => {
    if (!offer || !isHotel(offer)) {
      return resolveLocalizedConfigValue(intl, config.head.defaultDescription)
    }
    if (isBedbankOffer(offer)) {
      return intl.formatMessage({
        id: 'wG0PDtZQcUokceCp3PiU',
        defaultMessage: 'Book the best deal for {hotelName}, {city} with Luxury Escapes today. The world’s best escapes, handpicked by experts!',
        description: 'Meta description for bedbank hotel property search page, with hotel name and city interpolated',
      }, {
        hotelName: offer.name,
        city: offer.property.address.city,
      })
    }
    return getHeadDescription(offer, true)
  }, [offer, intl])

  return (
    <>
      <HeadData title={pageTitle} description={pageDescription} />
      {offer.property && <MetaSearchStructuredData offer={offer} filters={filters} />}
    </>
  )
}
export default MetaSearchPageHeadData
